import turfBuffer from "@turf/buffer";
import { isValidPolygonGeom } from "./assetRiskUtils"

export const clearBufferLayers = (map) => {
  const layers = map.getStyle().layers || [];
  
  const layersToRemove = [];
  layers.forEach((layer) => {
    if (layer.id.startsWith('buffer-') || layer.id.startsWith('buffer-outline-')) {
      layersToRemove.push(layer.id);
    }
  });

  layersToRemove.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  const sources = map.getStyle().sources || {};
  const sourcesToRemove = [];
  Object.keys(sources).forEach((sourceId) => {
    if (sourceId.startsWith('buffer-')) {
      sourcesToRemove.push(sourceId);
    }
  });

  sourcesToRemove.forEach((sourceId) => {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  });
};

export const addBuffer = (polygon, distance, index, map, polyId = null) => {
  const buffer = turfBuffer(polygon, distance, { units: 'meters' });

  const idPrefix = polyId ? `${polyId}-` : '';
  const bufferId = `buffer-${idPrefix}${index}`;
  const outlineId = `buffer-outline-${idPrefix}${index}`;

  const fillColor = polygon?.properties?.riskBufferFill ?? 'rgba(40, 175, 252, 0.3)';
  const outlineColor = polygon?.properties?.riskBufferOutline ?? 'rgba(40, 175, 252, 0.5)';

  if (!map.getSource(bufferId)) {
    map.addSource(bufferId, { type: 'geojson', data: buffer });
  } else {
    map.getSource(bufferId).setData(buffer);
  }

  const beforeId = firstDrawPolygonLayerId(map);

  const addFill = {
    id: bufferId,
    type: 'fill',
    source: bufferId,
    paint: {
      'fill-color': fillColor,
      'fill-outline-color': outlineColor,
    },
  };

  const addLine = {
    id: outlineId,
    type: 'line',
    source: bufferId,
    paint: {
      'line-color': outlineColor,
      'line-width': 2,
    },
  };

  if (!map.getLayer(bufferId)) {
    beforeId ? map.addLayer(addFill, beforeId) : map.addLayer(addFill);
  }
  if (!map.getLayer(outlineId)) {
    beforeId ? map.addLayer(addLine, beforeId) : map.addLayer(addLine);
  }
};

export const removeBuffersForPolyId = (map, polyId) => {
  if (!map || !polyId) return;
  
  const layers = map.getStyle().layers || [];
  layers
    .filter(l => l.id.startsWith(`buffer-${polyId}-`) || l.id.startsWith(`buffer-outline-${polyId}-`))
    .forEach(l => map.getLayer(l.id) && map.removeLayer(l.id));

  const sources = map.getStyle().sources || {};
  Object.keys(sources)
    .filter(id => id.startsWith(`buffer-${polyId}-`))
    .forEach(id => map.getSource(id) && map.removeSource(id));
};

export const syncBuffersFor = (poly, map, distances) => {
  if (!poly || !map) return;

  const isHome = !!(
    poly.properties?.isBuilding ||
    poly.properties?.assetContour === true
  );

  if (!isHome || poly.properties?.lotPerimeter) return;

  if (poly.geometry.type !== 'Polygon' && poly.geometry.type !== 'MultiPolygon') return;

  if (!isValidPolygonGeom(poly.geometry)) {
    removeBuffersForPolyId(map, poly.id);
    return;
  }

  const safeDistances = (Array.isArray(distances) ? distances : poly.properties?.buffers || [])
    .map(Number)
    .filter((d) => Number.isFinite(d) && d >= 0);

  (map.getStyle().layers || [])
    .filter(l => l.id.startsWith(`buffer-${poly.id}-`) || l.id.startsWith(`buffer-outline-${poly.id}-`))
    .forEach(l => map.getLayer(l.id) && map.removeLayer(l.id));

  Object.keys(map.getStyle().sources || {})
    .filter(s => s.startsWith(`buffer-${poly.id}-`))
    .forEach(s => map.getSource(s) && map.removeSource(s));

  if (!safeDistances.length) return;

  safeDistances.forEach((d, i) => addBuffer(poly, d, i, map, poly.id));
};

const firstDrawPolygonLayerId = (map) => {
  const layers = map.getStyle()?.layers || [];
  const l = layers.find(x => x?.id?.startsWith('gl-draw-polygon'));
  return l?.id || null;
};
